/**
 * Pure canvas drawing utilities. No DOM queries; all args passed in.
 * Used for rendering strokes on whiteboard and lesson overlay.
 */

import { LOGICAL_WIDTH, LOGICAL_HEIGHT } from './constants';

/**
 * Draw a single stroke on the canvas context.
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D context
 * @param {Array<[number, number]>} points - Array of [x, y] in logical coords
 * @param {string} color - CSS color string
 * @param {number} lineWidth - Stroke width
 * @param {{ width: number, height: number }} dimensions - Logical canvas dimensions
 * @param {{ width: number, height: number }} pixelDimensions - Actual pixel dimensions
 * @returns {boolean} True if drawn, false if invalid inputs
 */
export function drawStroke(ctx, points, color, lineWidth, dimensions, pixelDimensions) {
  if (!ctx || !points || !Array.isArray(points) || points.length < 1) {
    return false;
  }
  const lw = dimensions?.width;
  const lh = dimensions?.height;
  const pw = pixelDimensions?.width;
  const ph = pixelDimensions?.height;
  if (
    typeof lw !== 'number' ||
    typeof lh !== 'number' ||
    typeof pw !== 'number' ||
    typeof ph !== 'number' ||
    !Number.isFinite(lw) ||
    !Number.isFinite(lh) ||
    !Number.isFinite(pw) ||
    !Number.isFinite(ph) ||
    lw <= 0 ||
    lh <= 0 ||
    pw <= 0 ||
    ph <= 0
  ) {
    return false;
  }

  const scaleX = pw / lw;
  const scaleY = ph / lh;

  ctx.save();
  ctx.strokeStyle = color || '#000000';
  ctx.lineWidth = (lineWidth || 2) * Math.min(scaleX, scaleY);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();

  const [firstX, firstY] = points[0];
  if (!Number.isFinite(Number(firstX)) || !Number.isFinite(Number(firstY))) {
    ctx.restore();
    return false;
  }
  const px0 = Number(firstX) * scaleX;
  const py0 = Number(firstY) * scaleY;
  ctx.moveTo(px0, py0);

  for (let i = 1; i < points.length; i++) {
    const [px, py] = points[i];
    if (typeof px === 'number' && typeof py === 'number' && Number.isFinite(px) && Number.isFinite(py)) {
      ctx.lineTo(px * scaleX, py * scaleY);
    }
  }

  ctx.stroke();
  ctx.restore();
  return true;
}

/**
 * Clear canvas and redraw all strokes.
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D context
 * @param {Array<{ strokeId: string, points: Array<[number, number]>, color: string, lineWidth?: number, senderId?: string }>} strokes - Strokes to draw
 * @param {{ width: number, height: number }} logicalDimensions - Logical canvas size
 * @param {{ width: number, height: number }} pixelDimensions - Pixel canvas size
 * @returns {boolean} True if completed, false if invalid ctx
 */
export function redrawAllStrokes(ctx, strokes, logicalDimensions, pixelDimensions) {
  if (!ctx) return false;

  const pw = pixelDimensions?.width ?? 0;
  const ph = pixelDimensions?.height ?? 0;
  if (typeof pw !== 'number' || typeof ph !== 'number' || pw <= 0 || ph <= 0) {
    return false;
  }

  ctx.clearRect(0, 0, pw, ph);

  if (!strokes || !Array.isArray(strokes)) return true;

  const dims = logicalDimensions || { width: LOGICAL_WIDTH, height: LOGICAL_HEIGHT };
  const pxDims = pixelDimensions;

  for (const stroke of strokes) {
    if (stroke?.points?.length >= 1) {
      drawStroke(
        ctx,
        stroke.points,
        stroke.color || '#000000',
        stroke.lineWidth ?? 2,
        dims,
        pxDims
      );
    }
  }
  return true;
}
